import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Avatar,
  Tooltip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import { useNavigate, useParams } from 'react-router-dom';
import { productionsAPI, watchersAPI, WatcherProfile, licensingAPI, License } from '../services/api';
import { AdRecord } from '../types';
// Use API types to avoid enum/type mismatches
import { Production as ApiProduction } from '../services/api';
import LicenseSelector from '../components/common/LicenseSelector';
import LicenseStatusBadge from '../components/common/LicenseStatusBadge';
import PerformanceSummary from '../components/common/PerformanceSummary';

const ProductionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { productionId } = useParams<{ productionId: string }>();
  const [productions, setProductions] = useState<ApiProduction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newProduction, setNewProduction] = useState({
    production_name: '',
    description: '',
  });
  const [ads, setAds] = useState<AdRecord[] | null>(null);
  const [production, setProduction] = useState<ApiProduction | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [adSearchQuery, setAdSearchQuery] = useState('');
  const [watcherProfiles, setWatcherProfiles] = useState<Record<string, WatcherProfile>>({});

  // Actor licenses state
  const [actorLicenses, setActorLicenses] = useState<License[]>([]);
  const [showLicenseEditor, setShowLicenseEditor] = useState(false);

  // Load either production list or ads for a given production
  useEffect(() => {
    const load = async () => {
      setError(null);
      setLoading(true);
      try {
        if (productionId) {
          const idNum = Number(productionId);
          const [result, prod] = await Promise.all([
            productionsAPI.getAds(idNum),
            productionsAPI.get(idNum).catch(() => null),
          ]);
          setAds(result);
          setProduction(prod);
          // Resolve watcher profiles for avatars
          const ids = Array.from(new Set(result.flatMap(a => a.watchers || [])));
          if (ids.length > 0) {
            try {
              const profiles = await watchersAPI.resolve(ids);
              const map: Record<string, WatcherProfile> = {};
              profiles.forEach(p => { if (p && p.id) map[p.id] = p; });
              setWatcherProfiles(map);
            } catch (e) {
              // Non-fatal: fall back to initials
              setWatcherProfiles({});
            }
          } else {
            setWatcherProfiles({});
          }
        } else {
          // Try multiple creators to surface auto-created productions
          // No ownership filter anymore: fetch all productions
          const all = await productionsAPI.list();
          setProductions(all);
        }
      } catch (e: any) {
        setError(e.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [productionId]);

  // Load actor licenses for the production detail view
  useEffect(() => {
    const loadLicenses = async () => {
      if (!productionId) return;
      try {
        const { licenses } = await licensingAPI.getProductionLicenses(productionId);
        setActorLicenses(licenses);
      } catch {
        setActorLicenses([]);
      }
    };
    loadLicenses();
  }, [productionId]);

  const handleLinkActorLicense = async (licenseIds: string[]) => {
    if (!productionId) return;
    const currentIds = actorLicenses.map(l => l.license_id);
    const toAdd = licenseIds.filter(id => !currentIds.includes(id));
    const toRemove = currentIds.filter(id => !licenseIds.includes(id));

    try {
      for (const id of toAdd) {
        await licensingAPI.link(id, 'production', productionId);
      }
      for (const id of toRemove) {
        await licensingAPI.unlink(id, productionId);
      }
      const { licenses } = await licensingAPI.getProductionLicenses(productionId);
      setActorLicenses(licenses);
      setShowLicenseEditor(false);
    } catch (e: any) {
      setError(e.message || 'Failed to update licenses');
    }
  };

  const handleUnlinkActorLicense = async (licenseId: string) => {
    if (!productionId) return;
    try {
      await licensingAPI.unlink(licenseId, productionId);
      setActorLicenses(prev => prev.filter(l => l.license_id !== licenseId));
    } catch (e: any) {
      setError(e.message || 'Failed to remove license');
    }
  };

  const handleCreateProduction = () => {
    if (newProduction.production_name.trim()) {
      const production: ApiProduction = {
        production_id: Date.now(), // Mock ID
        production_name: newProduction.production_name,
        description: newProduction.description,
        created_by: 'current_user',
        ad_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        status: 'active',
      };
      
      setProductions(prev => [production, ...prev]);
      setCreateDialogOpen(false);
      setNewProduction({ production_name: '', description: '' });
    }
  };

  const handleViewProduction = (productionId: number) => {
    navigate(`/productions/${productionId}`);
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case 'active': return 'success';
      case 'completed': return 'primary';
      case 'archived': return 'default';
      default: return 'default';
    }
  };

  const filteredProductions = React.useMemo(() => {
    const list = !searchQuery.trim()
      ? [...productions]
      : productions.filter(p =>
          p.production_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          String(p.production_id).includes(searchQuery)
        );
    return list.sort((a, b) => b.production_id - a.production_id);
  }, [productions, searchQuery]);

  // Group data for ads view (must declare hooks at top-level)
  const groupedByCreative = React.useMemo(() => {
    if (!ads) return [] as Array<{ creative_number: number; title: string; ads: AdRecord[] }>;
      const byCreative = new Map<number, AdRecord[]>();
      for (const ad of ads) {
        const key = ad.creative_number;
        if (!byCreative.has(key)) byCreative.set(key, []);
        byCreative.get(key)!.push(ad);
      }

      const computeGroupTitle = (list: AdRecord[]): string => {
        const counts = new Map<string, number>();
        for (const a of list) {
          const title = a.name_en?.trim();
          if (title) counts.set(title, (counts.get(title) || 0) + 1);
        }
        if (counts.size > 0) {
          return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0][0];
        }
        return `Creative ${list[0]?.creative_number}`;
      };

    return Array.from(byCreative.entries()).map(([creative, list]) => ({
        creative_number: creative,
        title: computeGroupTitle(list),
        ads: list.sort((a, b) => a.ratio.localeCompare(b.ratio)),
    })).sort((a, b) => b.creative_number - a.creative_number);
  }, [ads]);

  const filteredGroupedAds = React.useMemo(() => {
    if (!adSearchQuery.trim()) return groupedByCreative;
    const q = adSearchQuery.toLowerCase();
    return groupedByCreative
      .map(group => ({
        ...group,
        ads: group.ads.filter(ad =>
          ad.ad_id.toLowerCase().includes(q) ||
          String(ad.creative_number).includes(q) ||
          (ad.name_en && ad.name_en.toLowerCase().includes(q)) ||
          (ad.ratio && ad.ratio.toLowerCase().includes(q))
        ),
      }))
      .filter(group => group.ads.length > 0);
  }, [groupedByCreative, adSearchQuery]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {productionId ? 'Loading ads...' : 'Loading productions...'}
        </Typography>
      </Container>
    );
  }

  // Detail view: ads for a production
  if (productionId && ads) {

    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <div>
            <Typography variant="h4" component="h1" gutterBottom>
              Production {productionId} — Ads
            </Typography>
            <Typography variant="body1" color="text.secondary">
              All ads within this production
            </Typography>
          </div>
          <Button onClick={() => navigate('/productions')}>Back to Productions</Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Performance Section */}
        {production && production.production_performance_updated_at && (
          <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
            <PerformanceSummary mode="production" data={production} />
          </Paper>
        )}

        {/* Actor Licenses Section */}
        <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">Actor Licenses</Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setShowLicenseEditor(!showLicenseEditor)}
            >
              {showLicenseEditor ? 'Done' : 'Edit'}
            </Button>
          </Box>

          {actorLicenses.length === 0 && !showLicenseEditor ? (
            <Typography variant="body2" color="text.secondary">No actor licenses linked</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {actorLicenses.map((license) => (
                <Box key={license.license_id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>{license.name}</Typography>
                  <LicenseStatusBadge status={license.status} />
                  {showLicenseEditor && (
                    <Button size="small" color="error" onClick={() => handleUnlinkActorLicense(license.license_id)}>
                      Remove
                    </Button>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {showLicenseEditor && (
            <Box sx={{ mt: 2 }}>
              <LicenseSelector
                licenseType="actor"
                selectedLicenseIds={actorLicenses.map(l => l.license_id)}
                onSelectionChange={handleLinkActorLicense}
              />
            </Box>
          )}

          {actorLicenses.some(l => l.status === 'expired' || l.status === 'expiring_soon') && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {actorLicenses.filter(l => l.status === 'expired').length > 0
                ? 'This production has expired actor licenses'
                : 'This production has actor licenses expiring soon'}
            </Alert>
          )}
        </Paper>

        <TextField
          fullWidth
          size="small"
          placeholder="Search ads by ID, name, creative number, or ratio..."
          value={adSearchQuery}
          onChange={(e) => setAdSearchQuery(e.target.value)}
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        {ads.length === 0 ? (
          <Paper elevation={1} sx={{ p: 6, textAlign: 'center' }}>
            <VideoLibraryIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              No ads in this production yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Upload ads to see them here
            </Typography>
          </Paper>
        ) : (
          <Paper elevation={2}>
            <TableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 90 }}>Creative</TableCell>
                    <TableCell>Ad ID</TableCell>
                    <TableCell sx={{ width: 80 }}>Ratio</TableCell>
                    <TableCell sx={{ width: 80 }}>Duration</TableCell>
                    <TableCell sx={{ width: 120 }}>Watchers</TableCell>
                    <TableCell sx={{ width: 80 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredGroupedAds.flatMap((group) =>
                    group.ads.map((ad) => (
                      <TableRow
                        key={ad.ad_id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/ads/${ad.ad_id}`)}
                      >
                        <TableCell sx={{
                          borderLeft: '4px solid',
                          borderLeftColor: ad.has_activity_7d ? 'success.main' : 'grey.300',
                        }}>
                          {ad.creative_number}
                        </TableCell>
                        <TableCell>{ad.ad_id}</TableCell>
                        <TableCell>{ad.ratio}</TableCell>
                        <TableCell>{Math.ceil(ad.duration)}s</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {ad.watchers && ad.watchers.length > 0 ? (
                              ad.watchers.map((uid) => {
                                const profile = watcherProfiles[uid];
                                return (
                                  <Tooltip title={profile?.display_name ? profile.display_name : `@${uid}`} key={uid}>
                                    <Avatar sx={{ width: 24, height: 24 }} src={profile?.profile_image || undefined}>
                                      {!profile?.profile_image && uid.substring(0, 2).toUpperCase()}
                                    </Avatar>
                                  </Tooltip>
                                );
                              })
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Button size="small" onClick={(e) => { e.stopPropagation(); navigate(`/ads/${ad.ad_id}`); }}>
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <div>
          <Typography variant="h3" component="h1" gutterBottom>
            Productions
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Managing every production under the family code
          </Typography>
        </div>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        size="small"
        placeholder="Search by name or ID..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />

      <Paper elevation={2}>
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 80 }}>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell sx={{ width: 80 }}>Ads</TableCell>
                <TableCell sx={{ width: 100 }}>Status</TableCell>
                <TableCell sx={{ width: 120 }}>Created</TableCell>
                <TableCell sx={{ width: 100 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProductions.map((production) => (
                <TableRow
                  key={production.production_id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleViewProduction(production.production_id)}
                >
                  <TableCell sx={{
                    borderLeft: '4px solid',
                    borderLeftColor: production.has_activity_7d ? 'success.main' : 'grey.300',
                  }}>
                    {production.production_id}
                  </TableCell>
                  <TableCell>{production.production_name}</TableCell>
                  <TableCell>{production.ad_count}</TableCell>
                  <TableCell>
                    <Chip
                      label={production.status}
                      color={getStatusColor(production.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{new Date(production.created_at).toLocaleDateString('en-GB')}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={(e) => { e.stopPropagation(); handleViewProduction(production.production_id); }}>
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {filteredProductions.length === 0 && !loading && productions.length > 0 && (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No productions match "{searchQuery}"
          </Typography>
        </Paper>
      )}

      {productions.length === 0 && !loading && (
        <Paper elevation={1} sx={{ p: 6, textAlign: 'center' }}>
          <VideoLibraryIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            No productions yet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Create your first production to start organizing your ad campaigns
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Production
          </Button>
        </Paper>
      )}

      <Fab
        color="primary"
        aria-label="add production"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => setCreateDialogOpen(true)}
      >
        <AddIcon />
      </Fab>

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Production</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Production Name"
            fullWidth
            variant="outlined"
            value={newProduction.production_name}
            onChange={(e) => setNewProduction(prev => ({ ...prev, production_name: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={newProduction.description}
            onChange={(e) => setNewProduction(prev => ({ ...prev, description: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateProduction}
            variant="contained"
            disabled={!newProduction.production_name.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProductionsPage; 